<script setup lang="ts">
import { Ref, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import CopyButton from '@/components/CopyButton.vue'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import InputText from 'primevue/inputtext'
import ConfirmDialog from 'primevue/confirmdialog'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { useConfirm } from 'primevue/useconfirm'
import { updateFolder } from './api.ts'
import { useLibraryStore } from './library.store.ts'
import { useUserStore } from './../authentication/user.store'
import { goalbookUrls } from '@/util/links.js'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { useLibraryFolderAnalytics } from './composables/useLibraryFolderAnalytics'

const confirm = useConfirm()
const props = defineProps({
  externalOpen: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['update:externalOpen'])
const userStore = useUserStore()
const libraryStore = useLibraryStore()
const analytics = getAnalyticsTracker()
const { currentFolder } = storeToRefs(libraryStore)

const calculateShareLinkFromToken = function (token: string | undefined): string {
  return token ? goalbookUrls.toolkitHome + '/share/folder/' + token : ''
}

const shareLink = ref(calculateShareLinkFromToken(currentFolder.value.share_token))
const visibleRef: Ref<boolean> = ref(props.externalOpen)
const updateError: Ref<boolean> = ref(false)
const updatingFolder: Ref<boolean> = ref(false)
const folderIsShared: Ref<boolean> = ref(shareLink.value ? true : false)

watch(
  () => currentFolder.value,
  () => {
    shareLink.value = calculateShareLinkFromToken(currentFolder.value.share_token)
    folderIsShared.value = shareLink.value ? true : false
  }
)

const closeModal = function (): void {
  visibleRef.value = false
  emit('update:externalOpen', false)
}

watch(
  () => props.externalOpen,
  () => {
    visibleRef.value = props.externalOpen
  }
)

watch(
  () => visibleRef.value,
  () => {
    emit('update:externalOpen', visibleRef.value)
  }
)

const confirmAndUpdateFolder = async function (newFlagValue: boolean) {
  if (!newFlagValue) {
    confirm.require({
      group: 'share-folder',
      header: 'Confirmation',
      message:
        `Are you sure you want to make this folder private? Anyone who has the view-only link will no longer be able to use it.`,
      accept: async () => {
        await sendUpdateFolder(newFlagValue)
      }
    })
  } else {
    await sendUpdateFolder(newFlagValue)
  }
}

const { getLegacyEventData, getEventData } = useLibraryFolderAnalytics()
const sendUpdateFolder = async function (newFlagValue: boolean) {
  updatingFolder.value = true
  try {
    const response = await updateFolder({
      userUuid: userStore.userId,
      folderUuid: currentFolder.value.uuid,
      updateParams: {
        share: `${newFlagValue}`
      },
    })
    analytics.sendEvent('Toggled Student Sharing', {
      ...getLegacyEventData.value,
      share: newFlagValue
    }) // to be deprecated
    analytics.sendEvent('Toggled Folder Sharing', {
      ...getEventData.value,
      share: newFlagValue
    })
    folderIsShared.value = newFlagValue
    shareLink.value = calculateShareLinkFromToken(response?.share_token)
    updateError.value = false
    libraryStore.fetchFolders(userStore.userId)
  } catch (e) {
    updateError.value = true
    console.error(e)
  }
  updatingFolder.value = false
}
</script>
<template>
  <GbDialog
    dialogId="share-folder-modal"
    v-model:visible="visibleRef"
    header="Send to caregivers or colleagues"
    :footer-buttons="true"
  >
    <template #bodyContent>
      <div class="flex flex-col gap-2">
        <div v-if="folderIsShared">
          <p class="pb-2">Anyone with the link <b>can view</b> this folder and everything in it.</p>
          <div class="flex items-stretch">
            <CopyButton
              :stringToCopy="shareLink"
              @copied="analytics.sendEvent('Copied Share Link', { ...getLegacyEventData, share_content_type: CONTENT_TYPES.STUDENT })"
              tooltipText="Link copied"
              buttonText="Copy View-only Link"
              customClass="rounded-r-none"
            >
              <template #icon>
                <span class="material-icons-round mr-1">link</span>
              </template>
            </CopyButton>
            <InputText
              class="grow rounded-l-none"
              :readonly="true"
              :value="shareLink"
              size="large"
              type="text"
              aria-label="Link to share folder"
            />
          </div>
          <p class="pt-4">
            <a
              v-if="!updatingFolder"
              @click="confirmAndUpdateFolder(false)"
              class="text-link cursor-pointer"
            >
              Make private
            </a>
            <span v-else>Turning off sharing...</span>
          </p>
        </div>
        <div v-else>
          <GbButton
            type="button"
            gbTheme="bluePrimary"
            size="small"
            @click="confirmAndUpdateFolder(true)"
          >
            <span class="material-icons-round mr-1">link</span>
            {{ updatingFolder ? 'Sharing...' : "Get View-only Link" }}
          </GbButton>
          <p class="pb-2 pt-6"><b>What is a View-only Link?</b></p>
          <p class="py-2">
            Anyone who has the view-only link for this folder <b>can view everything in it</b>. Only
            share the link with those who need it. To maintain privacy, do not post it in a publicly
            accessible place.
          </p>
          <p class="py-2">
            You can make it private at anytime, which will expire previous view-only links.
          </p>
        </div>
        <GbInlineMessage gbTheme="red" v-if="updateError">
          We couldn't update the folder. Try again later.
        </GbInlineMessage>
      </div>
    </template>
    <template #footerButtonRight>
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Done"
        @click="closeModal"
      ></GbButton>
    </template>
  </GbDialog>
  <ConfirmDialog group="share-folder" class="w-auto md:w-[38rem]">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col rounded-md bg-surface-50">
        <div class="mt-2 flex justify-between border-b border-gray-300 p-3">
          <span class="block pl-3 text-xl font-medium">{{ message.header }}</span>
          <GbButton
            size="small"
            rounded
            :icon-only="true"
            aria-label="close dialog"
            gbTheme="grayTertiary"
            class="mr-4 self-start"
            @click="rejectCallback"
          >
            <span class="material-icons-round">close</span>
          </GbButton>
        </div>
        <p class="py-5 pl-7 pr-12">{{ message.message }}</p>
        <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
          <GbButton
            label="Cancel"
            outlined
            @click="rejectCallback"
            gbTheme="grayOutlineSecondary"
          ></GbButton>
          <GbButton label="Make private" @click="acceptCallback" gbTheme="bluePrimary"></GbButton>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>
